/**
 * Clouds: vanilla "fancy" style blocky 3D clouds. One static mesh holding a
 * tiling of a seamless boolean cell pattern; it drifts downwind and is
 * snapped by whole pattern widths so it stays centred on the camera while
 * remaining anchored in world space.
 */

import * as THREE from 'three';
import { SEED } from '../constants';
import { mulberry32 } from '../noise';

/** Cloud cell footprint / thickness in blocks (vanilla: 12 x 4 x 12). */
const CELL = 12;
const THICKNESS = 4;
/** Altitude of the cloud underside (world height is 128). */
const ALTITUDE = 148;
/** Pattern is PATTERN x PATTERN cells and wraps seamlessly. */
const PATTERN = 40;
const COVERAGE = 0.43;
/** World units across one pattern repeat. */
const TILE = CELL * PATTERN;
/**
 * Pattern repeats across the mesh. Must be EVEN: the mesh is snapped by whole
 * TILEs, so the camera sits up to TILE/2 off-centre and the nearest mesh edge
 * is (TILES/2 - 0.5) * TILE away. 4 gives 720, comfortably past FADE_FAR
 * (a 3x3 tiling would only guarantee 480 and show its edge).
 */
const TILES = 4;
const HALF_EXTENT = (TILES * TILE) / 2;
/** Clouds are fully faded out by this distance. */
const FADE_FAR = 0.9 * (1.5 * TILE);
const FADE_NEAR = FADE_FAR * 0.55;
/** Wind along +x, blocks per second. */
const DRIFT_SPEED = 1.0;

/** Vanilla face shades. */
const SHADE_TOP = 1.0;
const SHADE_BOTTOM = 0.7;
const SHADE_X = 0.9;
const SHADE_Z = 0.8;

const VERT = /* glsl */ `
in float shade;

out float vShade;
out vec3 vView;

void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vShade = shade;
  vView = mv.xyz;
  gl_Position = projectionMatrix * mv;
}
`;

const FRAG = /* glsl */ `
precision highp float;

uniform vec3 uTint;
uniform vec3 uFogColor;
uniform float uOpacity;

in float vShade;
in vec3 vView;

out vec4 fragColor;

void main() {
  float dist = length(vView);
  // melt into the haze first, then fade out entirely so neither the tiling
  // edge nor a hard cut-off is ever visible
  float haze = smoothstep(${FADE_NEAR.toFixed(1)}, ${FADE_FAR.toFixed(1)}, dist);
  float alpha = uOpacity * (1.0 - smoothstep(${(FADE_FAR * 0.75).toFixed(1)}, ${FADE_FAR.toFixed(1)}, dist));
  if (alpha < 0.004) discard;
  vec3 color = mix(uTint * vShade, uFogColor, haze * 0.85);
  fragColor = vec4(color, alpha);
}
`;

export class Clouds {
  readonly mesh: THREE.Mesh;
  /** Overall cloud opacity 0..1. */
  opacity = 0.85;

  private readonly material: THREE.ShaderMaterial;
  private drift = 0;

  constructor() {
    const geometry = buildGeometry(buildPattern());

    const uniforms = {
      uTint: { value: new THREE.Color(1, 1, 1) },
      uFogColor: { value: new THREE.Color(0.5, 0.7, 1.0) },
      uOpacity: { value: this.opacity },
    };
    const base = {
      vertexShader: VERT,
      fragmentShader: FRAG,
      glslVersion: THREE.GLSL3,
      uniforms,
      transparent: true,
      side: THREE.DoubleSide, // the player can fly inside / above the layer
      // DoubleSide + transparent would otherwise draw back then front faces
      forceSinglePass: true,
    };

    // Depth pre-pass (vanilla does the same): lay down the nearest cloud
    // surface first, then shade only that surface. Without it, overlapping
    // translucent boxes blend on top of each other in triangle order.
    // Identical shader source => shared program => bit-identical depth.
    const depthMat = new THREE.ShaderMaterial({ ...base, colorWrite: false, depthWrite: true });
    this.material = new THREE.ShaderMaterial({ ...base, depthWrite: false });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.mesh.frustumCulled = false;
    // after opaque terrain, before translucent water (10)
    this.mesh.renderOrder = 8;
    this.mesh.position.y = ALTITUDE;

    const depth = new THREE.Mesh(geometry, depthMat);
    depth.frustumCulled = false;
    depth.renderOrder = 7.9;
    this.mesh.add(depth);
  }

  /**
   * tint: linear colour of lit cloud tops for the current time of day.
   * Leaves `mesh.position` in WORLD coordinates; a parent that is not at the
   * origin must subtract its own position afterwards.
   */
  update(dt: number, camera: THREE.Camera, tint: THREE.Color, fogColor: THREE.Color): void {
    this.drift = (this.drift + dt * DRIFT_SPEED) % TILE;

    const u = this.material.uniforms;
    (u.uTint.value as THREE.Color).copy(tint);
    (u.uFogColor.value as THREE.Color).copy(fogColor);
    u.uOpacity.value = this.opacity;

    // The pattern repeats every TILE, so jumping by whole TILEs is invisible.
    const cam = camera.position;
    this.mesh.position.set(
      this.drift + Math.round((cam.x - this.drift) / TILE) * TILE,
      ALTITUDE,
      Math.round(cam.z / TILE) * TILE
    );
  }
}

/** Deterministic wrapping PATTERN x PATTERN boolean cell map (1 = cloud). */
function buildPattern(): Uint8Array {
  const rand = mulberry32(SEED ^ 0xc10d5);
  const n = PATTERN * PATTERN;
  const value = new Float32Array(n);

  // Tileable value noise: each octave is a periodic lattice that divides
  // PATTERN, so the sum wraps exactly. Low octaves give vanilla's big clumps,
  // the per-cell octave gives the ragged blocky edges.
  const octaves: Array<[size: number, weight: number]> = [[5, 0.5], [10, 0.28], [20, 0.14], [40, 0.08]];
  for (const [size, weight] of octaves) {
    const lattice = new Float32Array(size * size);
    for (let i = 0; i < lattice.length; i++) lattice[i] = rand();
    const scale = size / PATTERN;
    for (let z = 0; z < PATTERN; z++) {
      for (let x = 0; x < PATTERN; x++) {
        const fx = (x + 0.5) * scale;
        const fz = (z + 0.5) * scale;
        const x0 = Math.floor(fx) % size;
        const z0 = Math.floor(fz) % size;
        const x1 = (x0 + 1) % size;
        const z1 = (z0 + 1) % size;
        let tx = fx - Math.floor(fx);
        let tz = fz - Math.floor(fz);
        tx = tx * tx * (3 - 2 * tx);
        tz = tz * tz * (3 - 2 * tz);
        const a = lattice[z0 * size + x0] + (lattice[z0 * size + x1] - lattice[z0 * size + x0]) * tx;
        const b = lattice[z1 * size + x0] + (lattice[z1 * size + x1] - lattice[z1 * size + x0]) * tx;
        value[z * PATTERN + x] += (a + (b - a) * tz) * weight;
      }
    }
  }

  // Threshold at a quantile so coverage is exact whatever the seed.
  const sorted = Float32Array.from(value).sort();
  const threshold = sorted[Math.floor(n * (1 - COVERAGE))];
  const cells = new Uint8Array(n);
  for (let i = 0; i < n; i++) cells[i] = value[i] >= threshold ? 1 : 0;
  return cells;
}

/** TILES x TILES repeats of the pattern as boxes, centred on the origin, y in [0, THICKNESS]. */
function buildGeometry(cells: Uint8Array): THREE.BufferGeometry {
  const at = (x: number, z: number): number =>
    cells[((z + PATTERN) % PATTERN) * PATTERN + ((x + PATTERN) % PATTERN)];

  // count quads per pattern repeat: top + bottom, plus sides facing empty cells
  let quads = 0;
  for (let z = 0; z < PATTERN; z++) {
    for (let x = 0; x < PATTERN; x++) {
      if (!at(x, z)) continue;
      quads += 2;
      if (!at(x - 1, z)) quads++;
      if (!at(x + 1, z)) quads++;
      if (!at(x, z - 1)) quads++;
      if (!at(x, z + 1)) quads++;
    }
  }
  quads *= TILES * TILES;

  const positions = new Float32Array(quads * 12);
  const shades = new Float32Array(quads * 4);
  const indices = new Uint32Array(quads * 6);
  let q = 0;

  // corners in counter-clockwise order seen from outside
  const quad = (
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    cx: number, cy: number, cz: number,
    dx: number, dy: number, dz: number,
    shade: number
  ): void => {
    positions.set([ax, ay, az, bx, by, bz, cx, cy, cz, dx, dy, dz], q * 12);
    shades.fill(shade, q * 4, q * 4 + 4);
    const v = q * 4;
    indices.set([v, v + 1, v + 2, v, v + 2, v + 3], q * 6);
    q++;
  };

  const y0 = 0;
  const y1 = THICKNESS;
  for (let tz = 0; tz < TILES; tz++) {
    for (let tx = 0; tx < TILES; tx++) {
      for (let z = 0; z < PATTERN; z++) {
        for (let x = 0; x < PATTERN; x++) {
          if (!at(x, z)) continue;
          const x0 = (tx * PATTERN + x) * CELL - HALF_EXTENT;
          const z0 = (tz * PATTERN + z) * CELL - HALF_EXTENT;
          const x1 = x0 + CELL;
          const z1 = z0 + CELL;
          quad(x0, y1, z0, x0, y1, z1, x1, y1, z1, x1, y1, z0, SHADE_TOP);
          quad(x0, y0, z0, x1, y0, z0, x1, y0, z1, x0, y0, z1, SHADE_BOTTOM);
          if (!at(x - 1, z)) quad(x0, y0, z0, x0, y0, z1, x0, y1, z1, x0, y1, z0, SHADE_X);
          if (!at(x + 1, z)) quad(x1, y0, z1, x1, y0, z0, x1, y1, z0, x1, y1, z1, SHADE_X);
          if (!at(x, z - 1)) quad(x1, y0, z0, x0, y0, z0, x0, y1, z0, x1, y1, z0, SHADE_Z);
          if (!at(x, z + 1)) quad(x0, y0, z1, x1, y0, z1, x1, y1, z1, x0, y1, z1, SHADE_Z);
        }
      }
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('shade', new THREE.BufferAttribute(shades, 1));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  return geometry;
}
